import { useClientTranslations } from "../../lib/i18n/client";
import type { Journey } from "../../types/journey";
import styles from "./JourneyHeader.module.css";
import { JourneyProgressCircle } from "./JourneyProgressCircle";

interface JourneyHeaderProps {
	journey: Journey;
	/** 0-based index of step where status === "current"; -1 if none. */
	currentStepIndex: number;
	totalSteps: number;
}

export function JourneyHeader({
	journey,
	currentStepIndex,
	totalSteps,
}: JourneyHeaderProps) {
	const { t } = useClientTranslations("journey");

	// Display "N/M" where N is 1-based and clamped >= 0
	const displayCurrent = currentStepIndex >= 0 ? currentStepIndex + 1 : 0;

	const treatmentLabel = t(`treatment.${journey.type}`, {
		defaultValue: journey.name,
	});

	// Status line: prefer the V2 patient label ("Day 6 of ~14") which carries the
	// per-step current-day-of-total. Falls back to the static "Active Treatments"
	// string when the step has no label or no current step is selected.
	// (Earlier "Day {order} of {title}" rendering — e.g., "Day 6 of Embryo
	// Development" — was medically misleading and has been removed.)
	const currentStep = journey.steps[currentStepIndex];
	const dayLabel = currentStep?.highlight?.currentDayLabel;
	const statusText = dayLabel
		? `${t("statusFallback")} | ${dayLabel}`
		: t("statusFallback");

	return (
		<div className={styles.header}>
			<JourneyProgressCircle current={displayCurrent} total={totalSteps} />
			<div className={styles.titleBlock}>
				<h1 className={styles.title}>{t("title")}</h1>
				<p className={styles.subLine}>{treatmentLabel}</p>
				<p className={styles.subLine}>{statusText}</p>
			</div>
		</div>
	);
}
